/* eslint-disable react/no-array-index-key */
import styled, { ThemeProvider } from 'styled-components';
import PropTypes from 'prop-types';
import { Collapse } from '@material-ui/core';
import P1 from '@base/P1/P1';
import H5 from '@base/H5/H5';
import { BgColors, DARK_THEME, Spacings } from '@styles/index';
import { QUESTION_TYPE_MAP } from '@constants/quiz';
import { useState } from 'react';
import getTheme from '@themes/index';
import DarkModeSwitch from '../CreationPreview/DarkModeSwitch';
import PreviewIcon from '../CreationPreview/PreviewIcon';
import QuestionWebPreview from './QuestionWebPreview';

const PreviewHeader = styled.div`
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: -${Spacings.SPACING_6B};
`;

const Wrapper = styled.div`
  height: 640px;
  overflow-y: scroll;
  background-color: ${({ theme }) => theme.colors.notfnHover};
  margin-top: ${Spacings.SPACING_5B};
  position: relative;
`;

const Banner = styled.div`
  background-color: ${({ color }) => color};
  width: 360px;
  height: 160px;
`;

const Fields = styled.div`
  position: absolute;
  top: 55px;
  width: 360px;
  padding-bottom: 300px;
`;

const Answer = styled(H5)`
  margin-right: ${Spacings.SPACING_4B};
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const Area = styled.div`
  width: 340px;
  min-height: ${({ minHeight }) => minHeight || '150px'};
  margin: auto;
  background-color: ${({ theme }) => theme.colors.whiteV3};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  display: ${({ isAnswer }) => isAnswer && 'flex'};
  overflow: hidden;
`;

const Text = styled(P1)`
  u {
    text-decoration: underline;
  }
  img {
    max-width: 276px;
    height: auto;
  }
  img[data-latex='true'] {
    max-width: 276px;
    height: auto;
    ${({ theme }) => theme.colors.type === DARK_THEME && 'filter: invert(1);'}
  }
`;

const Heading = styled(H5)`
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const Previews = styled.div`
  margin-top: -${Spacings.SPACING_6B};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const PreviewButton = styled.div`
  cursor: pointer;
`;

const QuestionPreview = ({ data, selectedLanguage }) => {
  const {
    question,
    questionType,
    singleSolution,
    upperLimit,
    lowerLimit,
    isIntegerRange,
    hasPassage,
    passage,
    options,
    solutionExplanation,
    hint
  } = data;

  const [isDarkModeOn, setDarkModeOn] = useState(false);
  const [showWebPreview, setShowWebPreview] = useState(false);

  const onToggleDarkMode = (isSelected) => {
    setDarkModeOn(isSelected);
  };

  const toggleShowWebPreview = () => {
    setShowWebPreview(!showWebPreview);
  };

  const selectedTheme = getTheme(isDarkModeOn);

  return (
    <>
      <PreviewHeader>
        <DarkModeSwitch
          onToggleDarkMode={onToggleDarkMode}
          isDarkModeOn={isDarkModeOn}
          label="Preview in Dark Mode"
        />
        <Previews>
          <PreviewButton>
            <PreviewIcon isDesktop={false} isSelected={!showWebPreview} />
          </PreviewButton>
          <PreviewButton onClick={toggleShowWebPreview}>
            <PreviewIcon isSelected={showWebPreview} />
          </PreviewButton>
        </Previews>
      </PreviewHeader>
      <ThemeProvider theme={selectedTheme}>
        <Wrapper>
          <Banner
            color={
              isDarkModeOn
                ? selectedTheme.colors.notfnHover
                : BgColors.SEA_GREEN
            }
          />
          <Fields>
            <Collapse in={hasPassage} timeout="auto" unmountOnExit>
              <Area>
                <Heading>Passage</Heading>
                <Text
                  color={selectedTheme.colors.fontGrey}
                  dangerouslySetInnerHTML={{
                    __html: passage[selectedLanguage]
                  }}
                />
              </Area>
            </Collapse>
            <Area>
              <Heading>Question 15</Heading>
              <Text
                color={selectedTheme.colors.fontGrey}
                dangerouslySetInnerHTML={{
                  __html: question[selectedLanguage]
                }}
              />
            </Area>
            {questionType === QUESTION_TYPE_MAP.INTEGER ? (
              <>
                <Area minHeight="66px">
                  <Heading>Answer</Heading>
                  <Text
                    color={selectedTheme.colors.fontGrey}
                    dangerouslySetInnerHTML={{
                      __html: singleSolution?.[selectedLanguage]
                    }}
                  />
                </Area>
                <Collapse in={isIntegerRange} timeout="auto" unmountOnExit>
                  <Area minHeight="66px">
                    <Heading>Lower limit</Heading>
                    <Text
                      color={selectedTheme.colors.fontGrey}
                      dangerouslySetInnerHTML={{
                        __html: lowerLimit?.[selectedLanguage]
                      }}
                    />
                  </Area>
                  <Area minHeight="66px">
                    <Heading>Upper limit</Heading>
                    <Text
                      color={selectedTheme.colors.fontGrey}
                      dangerouslySetInnerHTML={{
                        __html: upperLimit?.[selectedLanguage]
                      }}
                    />
                  </Area>
                </Collapse>
              </>
            ) : (
              <>
                {options.map((option, index) => (
                  <Area
                    isAnswer
                    minHeight="66px"
                    key={`${selectedLanguage}-${index}`}
                  >
                    <span>
                      <Answer>
                        {String.fromCharCode('A'.charCodeAt() + index)}
                      </Answer>
                    </span>
                    <Text
                      color={selectedTheme.colors.fontGrey}
                      dangerouslySetInnerHTML={{
                        __html: option[selectedLanguage]
                      }}
                    />
                  </Area>
                ))}
              </>
            )}
            <Area>
              <Heading>Solution</Heading>
              <Text
                color={selectedTheme.colors.fontGrey}
                dangerouslySetInnerHTML={{
                  __html: solutionExplanation[selectedLanguage]
                }}
              />
            </Area>
            <Area>
              <Heading>Hint</Heading>
              <Text
                color={selectedTheme.colors.fontGrey}
                dangerouslySetInnerHTML={{ __html: hint[selectedLanguage] }}
              />
            </Area>
          </Fields>
        </Wrapper>
        {showWebPreview && (
          <QuestionWebPreview
            showWebPreview={showWebPreview}
            data={data}
            selectedLanguage={selectedLanguage}
            onClose={toggleShowWebPreview}
          />
        )}
      </ThemeProvider>
    </>
  );
};

export default QuestionPreview;

QuestionPreview.propTypes = {
  data: PropTypes.objectOf(PropTypes.any),
  selectedLanguage: PropTypes.number
};

QuestionPreview.defaultProps = {
  data: {},
  selectedLanguage: -1
};
